import React, { useState } from "react";
import { FaTimes, FaCheck, FaTrash, FaEdit } from "react-icons/fa";
import { supabase } from "../../../../supabaseClient";
import {
  SecondaryButton,
  PrimaryButton,
  DangerButton,
  SoftButton,
} from "../../../UI/DesignStandards";
import DeleteAnimationWrapper from "../../../UI/DeleteAnimationWrapper";
import { useDeleteAnimation } from "../../../../hooks/useDeleteAnimation";
import { sanitizeErrorMessage } from "../../../../utility/errorSanitization";
import { notify } from "../../../UI/DesignStandards/Notifications/notify";
import { upsertTeamScoresVerification } from "../../../../utility/teamScoreVerification";
import {
  clearTeamRecapSheet,
  teamHasRecapSheet,
} from "../../../../utility/recapSheets";
import {
  AVERAGES_BLOCK_VERIFY_REASON,
  scoreReviewBlockedReason,
  showScoresWrittenWaitingCue,
  VERIFY_UNAVAILABLE_REASON,
  verifyBlockedOnlyOnAverages,
} from "../../../../utility/recapSheets/scoreReview";
import ScoresWrittenWaitingNote from "./ScoresWrittenWaitingNote";
import { tournamentPath } from "../../../../utility/tournamentUrls";

const TeamActions = ({
  team,
  tournamentId,
  tournamentSlug,
  isTeamVerified,
  canManage,
  canEnterScores,
  canVerifyTeamForEdit,
  onVerificationChange,
  onReview,
  onTeamDelete,
  onRecapDelete,
  verificationStatus,
  allBowlersVerified,
  teamHasScores,
  teamHasAllRequiredScores,
  importedTeamIds = [],
}) => {
  const {
    startDeleteAnimation,
    completeDeleteAnimation,
    isDeleting,
    cancelDeleteAnimation,
  } = useDeleteAnimation();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showRecapDeleteConfirmation, setShowRecapDeleteConfirmation] =
    useState(false);
  const hasRecap = teamHasRecapSheet(team);
  const payoutsLocked = Boolean(verificationStatus?.payouts?.isVerified);
  const waitingOnAverages = verifyBlockedOnlyOnAverages({
    team,
    payoutsLocked,
  });
  const scoresJustWritten = showScoresWrittenWaitingCue({
    team,
    importedTeamIds,
    payoutsLocked,
  });
  const showRecapVerify =
    !isTeamVerified &&
    hasRecap &&
    waitingOnAverages &&
    (canEnterScores || canManage);
  const hubBlockedReason = scoreReviewBlockedReason({
    team,
    payoutsLocked,
    canVerifyTeamForEdit,
  });

  const handleVerify = async () => {
    try {
      // Check if payouts are finalized using the new verification system
      if (verificationStatus?.payouts?.isVerified) {
        notify.warning("Payouts have been locked and cannot be changed.");
        return;
      }

      // Check if user has permission to verify teams
      if (!canManage) {
        notify.error("You don't have permission to verify teams.");
        return;
      }

      // Optimistic UI update - call the callback immediately
      if (onVerificationChange) {
        onVerificationChange(team.id, true);
      }

      await upsertTeamScoresVerification({
        tournamentId,
        teamId: team.id,
        isVerified: true,
      });
    } catch (error) {
      console.error("Error verifying team:", error);
      notify.error(sanitizeErrorMessage(error, "verify team"));
    }
  };

  const handleUnverify = async () => {
    try {
      // Check if payouts are finalized using the new verification system
      if (verificationStatus?.payouts?.isVerified) {
        notify.warning("Payouts have been locked and cannot be changed.");
        return;
      }

      // Check if user has permission to unverify teams
      if (!canManage) {
        notify.error("You don't have permission to unverify teams.");
        return;
      }

      // Optimistic UI update - call the callback immediately
      if (onVerificationChange) {
        onVerificationChange(team.id, false);
      }

      await upsertTeamScoresVerification({
        tournamentId,
        teamId: team.id,
        isVerified: false,
      });
    } catch (error) {
      console.error("Error unverifying team:", error);
      notify.error(sanitizeErrorMessage(error, "unverify team"));
    }
  };

  const handleDelete = async () => {
    try {
      // Check if payouts are finalized using the new verification system
      if (verificationStatus?.payouts?.isVerified) {
        notify.warning("Payouts have been locked and cannot be changed.");
        return;
      }

      // Start the delete animation
      startDeleteAnimation(team.id);

      // Mark team as deleted in the database
      const { error } = await supabase
        .from("teams")
        .update({
          isDeleted: true,
          deletedAt: new Date().toISOString(),
        })
        .eq("id", team.id);

      if (error) {
        console.error("Error deleting team:", error);
        notify.error(sanitizeErrorMessage(error, "delete team"));
        // Cancel the animation if deletion failed
        cancelDeleteAnimation(team.id);
        return;
      }

      // Complete the animation and remove from UI
      completeDeleteAnimation(team.id, () => {
        // Call parent delete handler
        if (onTeamDelete) {
          onTeamDelete(team.id);
        }
      });

      // Reset confirmation state
      setShowDeleteConfirmation(false);
    } catch (error) {
      console.error("Error deleting team:", error);
      notify.error("There was an error deleting the team.");
      // Cancel the animation if deletion failed
      cancelDeleteAnimation(team.id);
    }
  };

  const handleRecapDelete = async () => {
    try {
      if (payoutsLocked) {
        notify.warning("Payouts have been locked and cannot be changed.");
        return;
      }

      await clearTeamRecapSheet(team.id);
      onRecapDelete?.(team.id);
      setShowRecapDeleteConfirmation(false);
    } catch (error) {
      console.error("Error deleting recap:", error);
      notify.error(sanitizeErrorMessage(error, "delete recap"));
    }
  };

  const handleEdit = () => {
    // Check if payouts are finalized using the new verification system
    if (verificationStatus?.payouts?.isVerified) {
      notify.warning("Payouts have been locked and cannot be changed.");
      return;
    }

    // Navigate to team edit page
    window.location.href = tournamentPath(
      tournamentSlug || tournamentId,
      `/teams/${team.id}`
    );
  };

  return (
    <DeleteAnimationWrapper
      itemId={team.id}
      isDeleting={isDeleting(team.id)}
      className="w-full"
    >
      <div className="border-t border-brand-charcoal/10">
        {scoresJustWritten ? (
          <div className="px-4 pt-3" id={`verification-status-${team.id}`}>
            <ScoresWrittenWaitingNote />
          </div>
        ) : hasRecap && waitingOnAverages ? (
          <p
            id={`verification-status-${team.id}`}
            className="px-4 pt-3 text-sm text-brand-charcoal"
          >
            {AVERAGES_BLOCK_VERIFY_REASON}
          </p>
        ) : null}
        {/* Action buttons */}
        {(canManage || canEnterScores) && (
          <div className="flex flex-wrap justify-end items-center px-4 py-3">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canManage && (
                <>
                  <SecondaryButton
                    onClick={handleEdit}
                    className="!px-3 !py-2 text-sm"
                    aria-label={`Edit team ${team.teamName || "team"}`}
                    disabled={verificationStatus?.payouts?.isVerified}
                    title={
                      verificationStatus?.payouts?.isVerified
                        ? "Payouts have been locked and cannot be changed"
                        : `Edit team ${team.teamName || "team"}`
                    }
                  >
                    <FaEdit className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                    Edit
                  </SecondaryButton>
                  {!isTeamVerified && (
                    <div className="relative">
                      {!showDeleteConfirmation ? (
                        <SecondaryButton
                          onClick={() => {
                            if (verificationStatus?.payouts?.isVerified) return;
                            setShowRecapDeleteConfirmation(false);
                            setShowDeleteConfirmation(true);
                          }}
                          className="!px-3 !py-2 text-sm hover:bg-red-100 hover:text-red-700 hover:border-red-300"
                          aria-label={`Delete team ${team.teamName || "team"}`}
                          disabled={verificationStatus?.payouts?.isVerified}
                          title={
                            verificationStatus?.payouts?.isVerified
                              ? "Payouts have been locked and cannot be changed"
                              : `Delete team ${team.teamName || "team"}`
                          }
                        >
                          <FaTrash
                            className="h-3.5 w-3.5 mr-1.5"
                            aria-hidden="true"
                          />
                          Delete
                        </SecondaryButton>
                      ) : (
                        <div
                          className="flex items-center gap-2"
                          role="dialog"
                          aria-labelledby="delete-confirmation-title"
                        >
                          <span
                            id="delete-confirmation-title"
                            className="text-sm text-red-600 font-medium whitespace-nowrap"
                          >
                            Delete team?
                          </span>
                          <DangerButton
                            onClick={handleDelete}
                            className="rounded-lg font-display font-bold"
                            aria-label="Confirm delete team"
                            disabled={verificationStatus?.payouts?.isVerified}
                            title={
                              verificationStatus?.payouts?.isVerified
                                ? "Payouts have been locked and cannot be changed"
                                : "Confirm delete team"
                            }
                          >
                            Yes
                          </DangerButton>
                          <SecondaryButton
                            onClick={() =>
                              !verificationStatus?.payouts?.isVerified &&
                              setShowDeleteConfirmation(false)
                            }
                            className="!px-3 !py-2 text-sm"
                            aria-label="Cancel delete team"
                            disabled={verificationStatus?.payouts?.isVerified}
                            title={
                              verificationStatus?.payouts?.isVerified
                                ? "Payouts have been locked and cannot be changed"
                                : "Cancel delete team"
                            }
                          >
                            No
                          </SecondaryButton>
                        </div>
                      )}
                    </div>
                  )}
                  {!isTeamVerified && hasRecap && onRecapDelete && (
                    <div className="relative">
                      {!showRecapDeleteConfirmation ? (
                        <SecondaryButton
                          onClick={() => {
                            if (payoutsLocked) return;
                            setShowDeleteConfirmation(false);
                            setShowRecapDeleteConfirmation(true);
                          }}
                          className="!px-3 !py-2 text-sm hover:bg-red-100 hover:text-red-700 hover:border-red-300"
                          aria-label={`Delete recap for ${
                            team.teamName || "team"
                          }`}
                          disabled={payoutsLocked}
                          title={
                            payoutsLocked
                              ? "Payouts have been locked and cannot be changed"
                              : `Delete recap for ${team.teamName || "team"}`
                          }
                        >
                          <FaTrash
                            className="h-3.5 w-3.5 mr-1.5"
                            aria-hidden="true"
                          />
                          Delete recap
                        </SecondaryButton>
                      ) : (
                        <div
                          className="flex items-center gap-2"
                          role="dialog"
                          aria-labelledby="delete-recap-confirmation-title"
                        >
                          <span
                            id="delete-recap-confirmation-title"
                            className="text-sm text-red-600 font-medium whitespace-nowrap"
                          >
                            Delete recap?
                          </span>
                          <DangerButton
                            onClick={handleRecapDelete}
                            className="rounded-lg font-display font-bold"
                            aria-label="Confirm delete recap"
                            disabled={payoutsLocked}
                            title={
                              payoutsLocked
                                ? "Payouts have been locked and cannot be changed"
                                : "Confirm delete recap"
                            }
                          >
                            Yes
                          </DangerButton>
                          <SecondaryButton
                            onClick={() =>
                              !payoutsLocked &&
                              setShowRecapDeleteConfirmation(false)
                            }
                            className="!px-3 !py-2 text-sm"
                            aria-label="Cancel delete recap"
                            disabled={payoutsLocked}
                            title={
                              payoutsLocked
                                ? "Payouts have been locked and cannot be changed"
                                : "Cancel delete recap"
                            }
                          >
                            No
                          </SecondaryButton>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {!isTeamVerified &&
                (canEnterScores || canManage) &&
                hasRecap &&
                onReview && (
                <SoftButton
                  color="gold"
                  size="sm"
                  onClick={() => onReview(team.id)}
                  disabled={verificationStatus?.payouts?.isVerified}
                  title={
                    verificationStatus?.payouts?.isVerified
                      ? "Payouts have been locked and cannot be changed"
                      : "Review scores against the recap"
                  }
                  aria-label={`Review team ${team.teamName || "team"}`}
                  className="!px-3 !py-2"
                >
                  Review
                </SoftButton>
              )}

              {((!isTeamVerified && canEnterScores && !hasRecap) ||
                showRecapVerify) && (
                <PrimaryButton
                  onClick={handleVerify}
                  disabled={(() => {
                    const disabled =
                      showRecapVerify ||
                      !allBowlersVerified ||
                      verificationStatus?.payouts?.isVerified ||
                      !canVerifyTeamForEdit?.(team.id) ||
                      !teamHasAllRequiredScores;

                    return disabled;
                  })()}
                  title={(() => {
                    if (verificationStatus?.payouts?.isVerified) {
                      return "Payouts have been locked and cannot be changed";
                    }
                    if (!teamHasAllRequiredScores) {
                      return "Team must have all scores entered before verification";
                    }
                    if (!allBowlersVerified || showRecapVerify) {
                      return "All bowlers' averages must be verified first";
                    }
                    if (!canVerifyTeamForEdit?.(team.id)) {
                      return hubBlockedReason || VERIFY_UNAVAILABLE_REASON;
                    }
                    return "Verify team";
                  })()}
                  aria-label={(() => {
                    if (verificationStatus?.payouts?.isVerified) {
                      return "Cannot verify team - payouts are locked";
                    }
                    if (!teamHasAllRequiredScores) {
                      return "Cannot verify team - no scores entered";
                    }
                    if (!allBowlersVerified || showRecapVerify) {
                      return "Cannot verify team - some bowlers' averages are not verified";
                    }
                    if (!canVerifyTeamForEdit?.(team.id)) {
                      return hubBlockedReason || VERIFY_UNAVAILABLE_REASON;
                    }
                    return `Verify team ${team.teamName || "team"}`;
                  })()}
                  aria-describedby={
                    !allBowlersVerified
                      ? `verification-status-${team.id}`
                      : undefined
                  }
                >
                  <FaCheck className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                  Verify
                </PrimaryButton>
              )}

              {isTeamVerified && canManage && (
                <SecondaryButton
                  onClick={handleUnverify}
                  className="!px-3 !py-2 text-sm"
                  aria-label={`Unverify team ${team.teamName || "team"}`}
                  disabled={(() => {
                    if (verificationStatus?.payouts?.isVerified) {
                      return true; // Disable if payouts are locked
                    }
                    return false; // Enable if payouts are not locked
                  })()}
                  title={(() => {
                    if (verificationStatus?.payouts?.isVerified) {
                      return "Payouts have been locked and cannot be changed";
                    }
                    return `Unverify team ${team.teamName || "team"}`;
                  })()}
                >
                  <FaTimes className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
                  Unverify
                </SecondaryButton>
              )}
            </div>
          </div>
        )}

        {/* Verification status message for screen readers */}
        {!allBowlersVerified &&
          !scoresJustWritten &&
          !(hasRecap && waitingOnAverages) && (
          <div
            id={`verification-status-${team.id}`}
            className="sr-only"
            aria-live="polite"
          >
            {AVERAGES_BLOCK_VERIFY_REASON}
          </div>
        )}
      </div>
    </DeleteAnimationWrapper>
  );
};

export default TeamActions;
